// ═══════════════════════════════════════════════════════
// Photos — utilitaire central
// Stockage privé, URLs signées temporaires, storage_path = vérité
// ═══════════════════════════════════════════════════════

import { supabase } from './supabase.js';

const BUCKET = 'elixir-prep-photos';

// ── Repli miniature → original, sans toucher aux galeries ──
// Toute <img> dont la miniature échoue reçoit automatiquement l'URL de
// l'original (écouteur global en phase de capture : les erreurs d'image
// ne remontent pas, on les intercepte à la source).
const _fallbacks = new Map();
function registerFallback(thumbUrl, originalUrl) {
  _fallbacks.set(thumbUrl, originalUrl);
  if (_fallbacks.size > 2000) { const k = _fallbacks.keys().next().value; _fallbacks.delete(k); }
}
if (typeof window !== 'undefined' && !window.__elixirImgFallback) {
  window.__elixirImgFallback = true;
  window.addEventListener('error', (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'IMG') return;
    const orig = _fallbacks.get(el.src);
    if (orig) { _fallbacks.delete(el.src); el.src = orig; }
  }, true);
}

const SIGNED_URL_DURATION = 43200; // 12h (les pages restent ouvertes longtemps en entrepôt)

/**
 * Compresse une image côté client avant upload (économie stockage + bande passante).
 * - redimensionne à maxDim px sur le plus grand côté
 * - JPEG qualité ~78 % (largement suffisant pour des preuves qualité)
 * - ne renvoie JAMAIS plus gros que l'original, et laisse passer les non-images
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.78 } = {}) {
  try {
    if (!file || !(file.type || '').startsWith('image/')) return file;
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = URL.createObjectURL(file);
    });
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    if (!w || !h) return file;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const cw = Math.round(w * scale), ch = Math.round(h * scale);
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    canvas.getContext('2d').drawImage(img, 0, 0, cw, ch);
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', quality));
    URL.revokeObjectURL(img.src);
    if (!blob) return file;
    return blob.size < file.size ? blob : file; // jamais plus gros que l'original
  } catch {
    return file; // en cas de pépin, on envoie l'original (jamais bloquant)
  }
}

/**
 * Upload une photo (compressée) et retourne { storagePath, signedUrl }
 * Ne génère plus d'URL publique permanente.
 */
export async function uploadPhoto(file, folder = 'prep') {
  const toSend = await compressImage(file);
  const name = folder + '/' + Date.now() + '_' + Math.random().toString(36).slice(2) + '.jpg';
  const { error } = await supabase.storage.from(BUCKET).upload(name, toSend, { contentType: toSend.type || file.type || 'image/jpeg' });
  if (error) throw error;

  const signedUrl = await getSignedUrl(name);
  return { storagePath: name, signedUrl };
}

/**
 * BL scanné : la photo traitée (redressée, contrastée) est envoyée telle
 * quelle, sans recompression ; la photo d'origine est archivée à côté
 * (même nom + « _original »). Le nom porte la netteté mesurée et, si la
 * photo a été gardée malgré le flou, la mention « _flou-garde ».
 */
export async function uploadScanBL(traitee, meta, folder) {
  const base = folder + '/bl_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
    + '_n' + (meta?.nettete ?? 'x') + (meta?.force ? '_flou-garde' : '');
  const name = base + '.jpg';
  const { error } = await supabase.storage.from(BUCKET).upload(name, traitee, { contentType: 'image/jpeg' });
  if (error) throw error;
  let originalPath = null;
  if (meta?.original) {
    const { error: e2 } = await supabase.storage.from(BUCKET).upload(base + '_original.jpg', meta.original, { contentType: 'image/jpeg' });
    if (!e2) originalPath = base + '_original.jpg';
  }
  const signedUrl = await getSignedUrl(name);
  return { storagePath: name, signedUrl, originalPath };
}

/**
 * Génère une URL signée temporaire depuis un storage_path.
 * width (optionnel) : sert une version redimensionnée par Supabase
 * (transformation d'image) au lieu de l'original — énorme économie de
 * bande passante à l'affichage. Repli automatique sur l'original si la
 * transformation n'est pas disponible.
 * Fallback: si c'est déjà une URL complète (ancien format), la retourner telle quelle.
 */
export async function getSignedUrl(storagePath, width) {
  if (!storagePath) return null;

  // Ancien format (URL publique http) : le bucket étant privé, l'URL est
  // morte ("Load failed"). On extrait le chemin et on signe — auto-réparation.
  if (storagePath.startsWith('http')) {
    const extracted = extractPathFromUrl(storagePath);
    if (!extracted) return storagePath; // URL externe inconnue : telle quelle
    storagePath = extracted;
  }

  // resize 'contain' : sans lui, le service d'images garde la hauteur d'origine
  // et DÉCOUPE la photo (500 × 1600 pour un BL de 1200 × 1600) — photos coupées
  // sur les côtés, constaté le 21/10/2026. Avec 'contain', la photo est réduite
  // en gardant ses proportions (500 × 667).
  if (width) {
    try {
      const [t, o] = await Promise.all([
        supabase.storage.from(BUCKET).createSignedUrl(storagePath, SIGNED_URL_DURATION, { transform: { width, resize: 'contain', quality: 75 } }),
        supabase.storage.from(BUCKET).createSignedUrl(storagePath, SIGNED_URL_DURATION),
      ]);
      if (!t.error && t.data?.signedUrl) {
        // Repli automatique : si la miniature ne se charge pas (service de
        // transformation restreint ou quota dépassé), l'original prend sa place
        if (!o.error && o.data?.signedUrl) registerFallback(t.data.signedUrl, o.data.signedUrl);
        return t.data.signedUrl;
      }
    } catch { /* transformation indisponible : repli original ci-dessous */ }
  }

  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, SIGNED_URL_DURATION);
  if (error || !data?.signedUrl) {
    console.warn('Signed URL error for', storagePath, error);
    // Fallback public URL (au cas où le bucket est encore public)
    const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
    return pub?.publicUrl || null;
  }
  return data.signedUrl;
}

/**
 * Génère des URLs signées pour un tableau de storage_paths ou d'URLs mixtes.
 * Gère la compatibilité avec les anciens photo_urls publics.
 */
export async function getSignedUrls(pathsOrUrls, width) {
  if (!pathsOrUrls || pathsOrUrls.length === 0) return [];
  return Promise.all(pathsOrUrls.map(p => getSignedUrl(p, width)));
}

/**
 * Supprime une photo du storage.
 */
export async function deletePhoto(storagePath) {
  if (!storagePath || storagePath.startsWith('http')) return;
  await supabase.storage.from(BUCKET).remove([storagePath]);
}

/**
 * Extraire le storage_path depuis une URL publique (migration).
 * Ex: https://xxx.supabase.co/storage/v1/object/public/elixir-prep-photos/prep/123.jpg
 *   → prep/123.jpg
 */
export function extractPathFromUrl(url) {
  if (!url) return null;
  const marker = '/object/public/' + BUCKET + '/';
  const idx = url.indexOf(marker);
  if (idx >= 0) return url.substring(idx + marker.length);
  // Essayer avec /object/sign/
  const marker2 = '/object/sign/' + BUCKET + '/';
  const idx2 = url.indexOf(marker2);
  if (idx2 >= 0) {
    const path = url.substring(idx2 + marker2.length);
    return path.split('?')[0]; // Retirer les query params du signed URL
  }
  return null;
}
